import { Injectable, computed, signal } from '@angular/core';
import { Capacitor } from '@capacitor/core';
import { StatusBar, Style } from '@capacitor/status-bar';

export type ThemeMode = 'dark' | 'light';

export const STORAGE_KEY = 'lance_theme';

@Injectable({ providedIn: 'root' })
export class ThemeService {
  readonly theme = signal<ThemeMode>(this.readInitialTheme());
  readonly isLight = computed(() => this.theme() === 'light');

  constructor() {
    this.applyDomTheme(this.theme());
  }

  toggleTheme(): void {
    const next: ThemeMode = this.theme() === 'dark' ? 'light' : 'dark';
    this.setTheme(next);
  }

  setTheme(mode: ThemeMode): void {
    this.theme.set(mode);
    try {
      if (typeof localStorage !== 'undefined') {
        localStorage.setItem(STORAGE_KEY, mode);
      }
    } catch {
      // Ignore storage quota or private mode errors.
    }
    this.applyDomTheme(mode);
  }

  private readInitialTheme(): ThemeMode {
    try {
      if (typeof localStorage !== 'undefined') {
        const saved = localStorage.getItem(STORAGE_KEY);
        if (saved === 'light' || saved === 'dark') {
          return saved;
        }
      }
    } catch {
      // Fallback to dark.
    }
    return 'dark';
  }

  private applyDomTheme(mode: ThemeMode): void {
    if (typeof document === 'undefined') return;
    const isLight = mode === 'light';

    document.documentElement.classList.toggle('theme-light', isLight);
    document.documentElement.classList.toggle('dark', !isLight);

    if (document.body) {
      document.body.classList.toggle('theme-light', isLight);
      document.body.classList.toggle('dark', !isLight);
    }

    try {
      const metaTheme = document.querySelector('meta[name="theme-color"]');
      if (metaTheme) {
        metaTheme.setAttribute('content', isLight ? '#f5f5f5' : '#0a0a0a');
      }
      const metaScheme = document.querySelector('meta[name="color-scheme"]');
      if (metaScheme) {
        metaScheme.setAttribute('content', isLight ? 'light' : 'dark');
      }
    } catch {
      // DOM query failure safe fallback
    }

    this.syncNativeStatusBar();
  }

  syncNativeStatusBar(): void {
    if (!Capacitor.isNativePlatform()) return;
    try {
      const isLight = this.isLight();
      void StatusBar.setStyle({ style: isLight ? Style.Light : Style.Dark }).catch(() => {});
      void StatusBar.setBackgroundColor({ color: isLight ? '#f5f5f5' : '#0a0a0a' }).catch(() => {});
    } catch {
      // StatusBar not available
    }
  }
}
